import { useState } from "react";
import { FiCheck, FiLoader, FiUserPlus } from "react-icons/fi";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import { useTranslation } from "../context/TranslationContext";
import { followUser, unfollowUser } from "../api/profile";

/**
 * Follow / Following for one person. Following is public and moves nothing else — no
 * membership, chat or money — so it toggles straight away, without a confirmation.
 * "Follow back" when they already follow you.
 *
 * @param {(next: { isFollowing: boolean, followers?: number }) => void} onChange
 */
const FollowButton = ({ userId, isFollowing, followsYou = false, onChange, isDark = true, size = "md", setToast }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy || !userId) return;
    setBusy(true);
    // Just followed: say "Following" until the pointer comes back, not "Unfollow".
    setHover(false);
    const next = !isFollowing;
    onChange?.({ isFollowing: next });
    try {
      const response = next ? await followUser(userId) : await unfollowUser(userId);
      if (response?.success) onChange?.(response.data);
    } catch (error) {
      onChange?.({ isFollowing: !next });
      setToast?.(
        error?.response?.data?.code === "blocked"
          ? t("follows.cantFollow")
          : next
          ? t("follows.followFailed")
          : t("follows.unfollowFailed")
      );
    } finally {
      setBusy(false);
    }
  };

  const sizing = size === "sm" ? "px-3 py-1.5 text-footnote" : "px-4 py-2 text-subheadline";
  const look = isFollowing
    ? "border-separator-default bg-bg-raised text-label-primary hover:border-status-danger hover:text-status-danger"
    : "border-transparent bg-accent-default text-accent-on hover:bg-mint/90";
  const label = isFollowing
    ? hover
      ? t("follows.unfollow")
      : t("follows.isFollowing")
    : followsYou
    ? t("follows.followBack")
    : t("follows.follow");

  // The Space hero's button; the follower lists keep the smaller one below.
  if (size === "m") {
    return (
      <Button
        size="m"
        variant={isFollowing ? "gray" : "filled"}
        icon={<Icon name={isFollowing ? "check" : "plus"} size={20} />}
        loading={busy}
        onClick={toggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-pressed={isFollowing}
      >
        {label}
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={busy}
      aria-pressed={isFollowing}
      className={`inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-radius-sm border font-semibold transition disabled:opacity-60 ${sizing} ${look}`}
    >
      {busy ? (
        <FiLoader className="h-4 w-4 animate-spin" />
      ) : isFollowing ? (
        <FiCheck className="h-4 w-4" />
      ) : (
        <FiUserPlus className="h-4 w-4" />
      )}
      <span className="min-w-[4.5rem]">{label}</span>
    </button>
  );
};

export default FollowButton;
